/**
 * Related Conversations: horizontally scrollable cards linking to other curated
 * Questions in the silo.
 *
 * The selected set, its ordering, and each related space's comment-engagement
 * preview (comment count, latest comment, participant count) all come from
 * the `spaces.relatedSpaces` oRPC sub-resource via the `data/related-spaces`
 * hooks.
 *
 * The recirculation slider has measurement gymnastics (manual scroll-x
 * tracking, throttled `onScroll`, card-width-aware scroll-to) because it
 * predates `FlatList` paging on web. The single-card case bails out to a larger
 * horizontal layout (`singleCard` flag) so a lonely related conversation
 * doesn't look like an empty carousel.
 */

import { ChevronLeft, ChevronRight } from '@carbon/icons-react';
import { ORPCError } from '@orpc/client';
import { displayTextOf } from '@repo/shared/orpc/multi-language';
import type { CommentTeaserPreview } from '@repo/shared/orpc/procedures/spaces';
import { type ColorTokens, useTheme } from '@repo/theme';
import { Banner } from 'component/banner';
import { AdaptiveWidth, HorizBox, Pad, PadBox, SeparatedBox } from 'component/basics';
import { SubtleButton } from 'component/button';
import { spacings } from 'component/constants';
import { ListSelector, SearchField } from 'component/form';
import { Checkbox } from 'component/form/checkbox';
import { DataVizText, Heading, UtilityText } from 'component/text';
import { useSiloLanguage } from 'component/translation';
import {
    type RelatedSpace,
    useRelatedSpaces,
    useReorderRelatedSpaces,
    useSelectRelatedSpace,
    useUnselectRelatedSpace,
} from 'data/related-spaces';
import { useSpaceSearch } from 'data/spaces';
import { CommentStack } from 'feature/teaser/editorial-question';
import { useRef, useState } from 'react';
import { type DimensionValue, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import type { QuestionConfig } from 'structure/question';
import { showApiErrorToast } from 'system/orpc-errors';
import { useIsAdmin } from 'util/admin';
import { trackEvent } from 'util/analytics';
import { useDatastore, useInstanceKey, useIsInSidebar } from 'util/datastore';
import { useConfig } from 'util/features-hooks';
import { LAYOUT_LEFT_RAIL_WIDTH } from 'util/layout';
import { t } from 'util/stdtypes';

const CARD_WIDTH_MOBILE = 285;
const CARD_WIDTH_DESKTOP = 335;

export type CardsData = RelatedSpace;

export function RelatedConversations() {
    const spaceKey = useInstanceKey();
    const { relatedSpaces } = useRelatedSpaces(spaceKey);
    const [narrow, setNarrow] = useState(false);
    const { relatedConversationsTitle } = useConfig<QuestionConfig>();
    const isInSidebar = useIsInSidebar();

    if (!relatedSpaces.length) {
        return null;
    }

    const content = (
        <PadBox horiz={spacings.lg} top={spacings.xl2} vert={spacings.xl2}>
            <AdaptiveWidth breakpoint={400} setNarrow={setNarrow}>
                <Heading label={relatedConversationsTitle} type='large' weight='medium' />
                <Pad size={spacings.lg} />
                <RecirculationSlider cardsData={relatedSpaces} controls narrow={narrow} />
            </AdaptiveWidth>
        </PadBox>
    );

    // In standalone mode, constrain width to match content area (no full-width
    // background)
    if (!isInSidebar) {
        return <View style={{ maxWidth: LAYOUT_LEFT_RAIL_WIDTH, width: '100%', alignSelf: 'center' }}>{content}</View>;
    }

    return content;
}

type RelatedConversationCardProps = RelatedSpace & {
    singleCard?: boolean;
    narrow?: boolean;
};

/**
 * Related-conversations wrapper: navigates to the linked Question on
 * press, delegating rendering to the presentational `ConversationCard`.
 */
function RelatedConversationCard({ text, singleCard = false, spaceKey, narrow = false, preview }: RelatedConversationCardProps) {
    const datastore = useDatastore();
    const isoLocale = useSiloLanguage();

    return (
        <ConversationCard
            commentPreview={preview?.commentPreview ?? null}
            narrow={narrow}
            onPress={() => {
                trackEvent(datastore, 'related-conversation-click', { structureKey: 'question', instanceKey: spaceKey });
                datastore.gotoInstance({ structureKey: 'question', instanceKey: spaceKey });
            }}
            participantCount={preview?.participantCount || 0}
            singleCard={singleCard}
            testID={`conversation-card-${spaceKey}`}
            text={displayTextOf(text, isoLocale)}
        />
    );
}

export type ConversationCardProps = {
    text: string;
    participantCount: number;
    commentPreview: CommentTeaserPreview | null;
    onPress?: () => void;
    singleCard?: boolean;
    narrow?: boolean;
    testID?: string;
    spread?: boolean;
};

/**
 * Presentational conversation card: the green entry-point tile showing a
 * conversation title, participant count, and a preview comment stack. Data and
 * navigation are supplied by the caller so the same visual can back both the
 * Related Conversations recirculation slider and the Conversation Home page.
 */
export function ConversationCard({
    text,
    participantCount,
    commentPreview,
    onPress,
    singleCard = false,
    narrow = false,
    testID,
    spread = false,
}: ConversationCardProps) {
    const formatParams = {
        count: participantCount,
        singular: t('related_conversations.participant_singular', 'participant'),
        plural: t('related_conversations.participant_plural', 'participants'),
    };
    const { colors } = useTheme();
    const s = createCardStyle({ singleCard, narrow, colors, spread });

    return (
        <Pressable onPress={onPress} style={s.card} testID={testID}>
            <View style={{ flexShrink: 1 }}>
                {narrow ? (
                    <DataVizText color={colors.colorTextOnEntryPointColorBg} text={text} type='large' />
                ) : (
                    <Heading color={colors.colorTextOnEntryPointColorBg} text={text} type='large' weight='medium' />
                )}
                <Pad size={spacings.xs} />
                <UtilityText
                    color={colors.colorTextOnEntryPointColorBg}
                    formatParams={formatParams}
                    label={t('related_conversations.participant_count', '{count} {noun}')}
                    type='tiny'
                />
            </View>
            <View style={s.spacer}>
                <CommentStack commentPreview={commentPreview ?? null} width={commentStackWidth({ singleCard, narrow, spread })} />
            </View>
        </Pressable>
    );
}

// A spread card sizes to its grid cell, so its stack has to fill the card
// rather than a fixed width; a wide single card keeps the fixed width the
// horizontal layout was designed around. A slider card is already a fixed
// width, so its stack takes none and stretches as it always has.
function commentStackWidth({
    singleCard,
    narrow,
    spread,
}: {
    singleCard: boolean;
    narrow: boolean;
    spread: boolean;
}): DimensionValue | undefined {
    if (singleCard && !narrow) return 245;
    return spread ? '100%' : undefined;
}

const createCardStyle = ({
    singleCard,
    narrow,
    spread,
    colors,
}: {
    singleCard: boolean;
    narrow: boolean;
    spread: boolean;
    colors: ColorTokens;
}) =>
    StyleSheet.create({
        card: {
            flexDirection: singleCard && !narrow ? 'row' : 'column',
            justifyContent: singleCard && !narrow ? 'space-between' : 'flex-start',
            width: singleCard || spread ? 'auto' : narrow ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP,
            padding: spacings.lg,
            backgroundColor: colors.colorEntryPointColorGreen,
            borderRadius: 12,
        },
        spacer: {
            paddingTop: singleCard && !narrow ? 0 : spacings.lg,
            paddingLeft: singleCard && !narrow ? spacings.lg : 0,
        },
    });

function handleScrollTo({
    scrollRef,
    x,
    step,
    maxScrollX,
    increment = false,
}: {
    scrollRef: any;
    x: number;
    step: number;
    maxScrollX: number;
    increment?: boolean;
}): void {
    const position = Math.min(maxScrollX, Math.max(0, increment ? x + step : x - step));
    scrollRef.scrollTo({ x: position, y: 0, animated: true });
}

const RecirculationScrollView = ({
    cardsData,
    narrow,
    isAdmin,
    scrollRef,
    setViewportWidth,
    setScrollX,
    lastScrollUpdate,
}: {
    cardsData: CardsData[];
    narrow: boolean;
    isAdmin: boolean | null;
    scrollRef: React.RefObject<any>;
    setViewportWidth: (width: number) => void;
    setScrollX: (x: number) => void;
    lastScrollUpdate: React.MutableRefObject<number>;
}) => {
    const s = RecirculationScrollViewStyle;

    return (
        <ScrollView
            contentContainerStyle={{ gap: spacings.lg }}
            horizontal
            onLayout={(event) => setViewportWidth(event.nativeEvent.layout.width)}
            onScroll={({ nativeEvent }) => {
                const now = Date.now();
                if (now - lastScrollUpdate.current > 50) {
                    setScrollX(nativeEvent.contentOffset.x);
                    lastScrollUpdate.current = now;
                }
            }}
            ref={scrollRef}
            scrollEventThrottle={16}
            showsHorizontalScrollIndicator={false}
        >
            {cardsData.map((data) => (
                <View key={data.spaceKey} style={s.container}>
                    <RelatedConversationCard narrow={narrow} singleCard={cardsData.length === 1 && !isAdmin} {...data} />
                </View>
            ))}
        </ScrollView>
    );
};

const RecirculationScrollViewStyle = StyleSheet.create({
    container: {
        flexDirection: 'row',
    },
});

const RecirculationControls = ({
    scrollRef,
    x,
    step,
    maxScrollX,
}: {
    scrollRef: React.RefObject<any>;
    x: number;
    step: number;
    maxScrollX: number;
}) => {
    const { colors } = useTheme();
    return (
        <>
            <Pad size={spacings.lg} />
            <HorizBox center spread>
                <SubtleButton
                    ariaHidden={true}
                    color={colors.colorTextSecondary}
                    disabled={Math.round(x) === 0}
                    icon={ChevronLeft}
                    label={t('related_conversations.previous_button', 'Previous')}
                    onPress={() => handleScrollTo({ scrollRef: scrollRef.current!, x, step, maxScrollX })}
                    tabIndex={-1}
                />
                <SubtleButton
                    ariaHidden={true}
                    color={colors.colorTextSecondary}
                    disabled={Math.round(x) >= maxScrollX}
                    icon={ChevronRight}
                    iconProps={{ after: true }}
                    label={t('related_conversations.next_button', 'Next')}
                    onPress={() => handleScrollTo({ scrollRef: scrollRef.current!, x, step, maxScrollX, increment: true })}
                    tabIndex={-1}
                />
            </HorizBox>
        </>
    );
};

export type RecirculationSliderProps = { cardsData: CardsData[]; controls?: boolean; narrow?: boolean };

/**
 * The viewport width is state, not a ref: the Previous/Next handlers close over
 * it at render, so a width measured after the last render must trigger one.
 */
export function RecirculationSlider({ cardsData, controls = false, narrow = false }: RecirculationSliderProps) {
    const scrollRef = useRef(null);
    const [scrollX, setScrollX] = useState(0);
    const [viewportWidth, setViewportWidth] = useState(0);
    const lastScrollUpdate = useRef(0);
    const isAdmin = useIsAdmin();

    const cardWidthActual = narrow ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP;
    const spacing = spacings.lg;
    const contentWidth = cardsData.length * cardWidthActual + (cardsData.length - 1) * spacing;
    const maxScrollX = Math.max(0, contentWidth - viewportWidth);

    if (cardsData === undefined || cardsData.length === 0) {
        return null;
    }

    if (cardsData.length === 1) {
        return (
            <RelatedConversationCard
                key={`ConversationCard-${cardsData[0].spaceKey}`}
                narrow={narrow}
                singleCard={true}
                {...cardsData[0]}
            />
        );
    }

    return (
        <>
            <RecirculationScrollView
                cardsData={cardsData}
                isAdmin={isAdmin}
                lastScrollUpdate={lastScrollUpdate}
                narrow={narrow}
                scrollRef={scrollRef}
                setScrollX={setScrollX}
                setViewportWidth={setViewportWidth}
            />
            {controls && (
                <RecirculationControls maxScrollX={maxScrollX} scrollRef={scrollRef} step={cardWidthActual + spacing} x={scrollX} />
            )}
        </>
    );
}

export function RelatedConversationsEditScreenWidget() {
    const spaceKey = useInstanceKey();
    const datastore = useDatastore();
    const { relatedSpaces } = useRelatedSpaces(spaceKey);
    const isoLocale = useSiloLanguage();
    const sortedItems = relatedSpaces.map((item) => ({ key: item.spaceKey, text: displayTextOf(item.text, isoLocale) }));
    const { selectRelatedSpaceAsync } = useSelectRelatedSpace();
    const { unselectRelatedSpaceAsync } = useUnselectRelatedSpace();
    const { reorderRelatedSpacesAsync } = useReorderRelatedSpaces();

    // Surfaces the server's cap (`RELATED_SPACE_LIMIT_REACHED`) as a
    // `ListSelector` form error instead of letting it reject the promise,
    // which would otherwise leave the Add button stuck mid-submit. Any other
    // failure gets the same treatment, with a generic message.
    async function onAddItems(keys: string[]) {
        for (const key of keys) {
            try {
                await selectRelatedSpaceAsync({ relatedSpaceKey: key });
            } catch (error) {
                if (error instanceof ORPCError && error.code === 'RELATED_SPACE_LIMIT_REACHED') {
                    return {
                        error: t(
                            'related_conversations.limit_reached_error',
                            'This space already has four related spaces selected. Remove one before adding another.',
                        ),
                    };
                }
                return { error: t('error.api', 'Something went wrong') };
            }
        }
        return { error: null };
    }

    async function onDeleteItem(key: string) {
        try {
            await unselectRelatedSpaceAsync({ relatedSpaceKey: key });
        } catch (error) {
            showApiErrorToast({ datastore, error });
        }
    }

    async function handleChangeSortOrder(orderedKeys: string[]) {
        try {
            await reorderRelatedSpacesAsync({ order: orderedKeys });
        } catch (error) {
            showApiErrorToast({ datastore, error });
        }
    }

    return (
        <ListSelector
            addHintLabel={t('related_conversations.add_hint', 'Add up to four spaces maximum.')}
            addLabel={t('related_conversations.add_spaces_button', 'Add spaces')}
            addWidget={AddSpaces}
            items={sortedItems}
            label={t('related_conversations.title', 'Related conversations')}
            onAddItems={onAddItems}
            onChangeSortOrder={handleChangeSortOrder}
            onDeleteItem={onDeleteItem}
            reorderable
        />
    );
}

type AddSpacesProps = {
    setToAdd: (keys: string[]) => void;
};

function AddSpaces({ setToAdd }: AddSpacesProps) {
    const s = AddSpacesStyle;
    const { colors } = useTheme();
    const spaceKey = useInstanceKey();
    const [search, setSearch] = useState('');
    const { spaces, isLoading } = useSpaceSearch({ search, status: ['active'] });
    const { relatedSpaces } = useRelatedSpaces(spaceKey);
    const [selectedQuestions, setSelectedQuestions] = useState<Record<string, boolean>>({});
    const isoLocale = useSiloLanguage();
    const filteredSpaces = spaces.filter(
        (space) => space.spaceKey !== spaceKey && !relatedSpaces.some((relatedSpace) => relatedSpace.spaceKey === space.spaceKey),
    );

    function changeQuestionSelected(key: string, selected: boolean): void {
        const newSelectedQuestions: Record<string, boolean> = { ...selectedQuestions, [key]: selected };
        setSelectedQuestions(newSelectedQuestions);
        setToAdd(Object.keys(newSelectedQuestions).filter((key: string) => newSelectedQuestions[key]));
    }

    const hasResults = filteredSpaces.length > 0;
    const showNoResults = !isLoading && !hasResults;

    return (
        <View>
            <SearchField onChange={setSearch} placeholderLabel={t('related_conversations.search_placeholder', 'Search')} value={search} />
            <Pad size={24} />
            {hasResults && (
                <ScrollView style={s.scroll}>
                    <SeparatedBox>
                        {filteredSpaces.map((space) => (
                            <Checkbox
                                key={space.spaceKey}
                                onChange={(selected) => changeQuestionSelected(space.spaceKey, selected)}
                                text={displayTextOf(space.name, isoLocale)}
                                value={selectedQuestions[space.spaceKey]}
                            />
                        ))}
                    </SeparatedBox>
                </ScrollView>
            )}
            {showNoResults && (
                <Banner color={colors.colorSurfaceBannerInfo}>
                    <UtilityText
                        label={
                            search
                                ? t('related_conversations.no_spaces_found', 'No spaces found')
                                : t('related_conversations.no_spaces_available', 'No spaces available')
                        }
                    />
                </Banner>
            )}
        </View>
    );
}

const AddSpacesStyle = StyleSheet.create({
    scroll: {
        flex: 1,
        maxHeight: 240,
    },
});
